import { useCallback, useEffect, useReducer, useRef } from "react";
import { api } from "@/api/client";
import { putToGcs } from "@/shared/upload/put-to-gcs";
import { runWithConcurrency } from "@/shared/upload/run-with-concurrency";
import { UPLOAD_CONCURRENCY } from "@/config";
import type { QueueFile } from "./use-upload-queue";

export type UploadPhase = "idle" | "starting" | "uploading" | "finishing" | "done" | "error";

export type FileProgress = {
  pct: number;
  loaded: number;
  total: number;
  speedBps: number;
  status: "pending" | "uploading" | "done" | "failed";
  error?: string;
};

type State = {
  phase: UploadPhase;
  error: string | null;
  uploadId: string | null;
  gcsPrefix: string | null;
  progressById: Record<string, FileProgress>;
  overallPct: number;
};

type Action =
  | { type: "RESET" }
  | { type: "SET_PHASE"; phase: UploadPhase }
  | { type: "SET_ERROR"; error: string }
  | { type: "SET_PROGRESS"; id: string; progress: FileProgress }
  | { type: "SET_OVERALL"; pct: number }
  | { type: "SET_DONE"; uploadId: string; gcsPrefix: string };

const initial: State = {
  phase: "idle",
  error: null,
  uploadId: null,
  gcsPrefix: null,
  progressById: {},
  overallPct: 0,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "RESET":
      return { ...initial };
    case "SET_PHASE":
      return { ...state, phase: action.phase, error: action.phase === "error" ? state.error : null };
    case "SET_ERROR":
      return { ...state, phase: "error", error: action.error };
    case "SET_PROGRESS":
      return { ...state, progressById: { ...state.progressById, [action.id]: action.progress } };
    case "SET_OVERALL":
      return { ...state, overallPct: action.pct };
    case "SET_DONE":
      return {
        ...state,
        phase: "done",
        uploadId: action.uploadId,
        gcsPrefix: action.gcsPrefix,
        overallPct: 100,
      };
    default:
      return state;
  }
}

function formatSpeed(bps: number) {
  if (bps < 1024) return `${Math.round(bps)} B/s`;
  if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(1)} KB/s`;
  return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
}

export { formatSpeed };

function uploadFilename(qf: QueueFile): string {
  if (qf.relativePath.includes("/")) {
    return qf.relativePath.replace(/\//g, "__");
  }
  return qf.displayName;
}

export function useUpload() {
  const [state, dispatch] = useReducer(reducer, initial);
  const abortRef = useRef<AbortController | null>(null);
  const speedRef = useRef<Record<string, { lastLoaded: number; lastTs: number }>>({});

  useEffect(() => () => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    speedRef.current = {};
    dispatch({ type: "RESET" });
  }, []);

  const start = useCallback(async (files: QueueFile[], note?: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    speedRef.current = {};

    dispatch({ type: "SET_PHASE", phase: "starting" });

    try {
      const payload = files.map((f) => ({
        filename: uploadFilename(f),
        size: f.file.size,
        content_type: f.file.type || "application/octet-stream",
      }));

      const startRes = await api.startUpload(payload);
      if (controller.signal.aborted) return;

      if (startRes.items.length !== files.length) {
        dispatch({
          type: "SET_ERROR",
          error: "Upload setup returned an unexpected file list. Please try again.",
        });
        return;
      }

      const signedByIndex = startRes.items;

      const canonicalFiles = files.map((qf, idx) => {
        const item = signedByIndex[idx];
        return {
          original_filename: uploadFilename(qf),
          filename: item.filename,
          size: qf.file.size,
          content_type: qf.file.type || "application/octet-stream",
        };
      });

      dispatch({ type: "SET_PHASE", phase: "uploading" });

      const failed: string[] = [];
      const loadedById: Record<string, number> = {};
      const totalBytes = files.reduce((n, f) => n + f.file.size, 0);

      const updateOverall = () => {
        const loaded = Object.values(loadedById).reduce((n, v) => n + v, 0);
        const pct = totalBytes > 0 ? Math.min(99, Math.round((loaded / totalBytes) * 100)) : 0;
        dispatch({ type: "SET_OVERALL", pct });
      };

      const tasks = files.map((qf, idx) => async () => {
        const item = signedByIndex[idx];
        if (!item?.signed_put_url) {
          failed.push(qf.id);
          dispatch({
            type: "SET_PROGRESS",
            id: qf.id,
            progress: {
              pct: -1,
              loaded: 0,
              total: qf.file.size,
              speedBps: 0,
              status: "failed",
              error: "Could not prepare upload for this file.",
            },
          });
          return;
        }

        const started = performance.now();
        speedRef.current[qf.id] = { lastLoaded: 0, lastTs: started };

        try {
          await putToGcs(
            item.signed_put_url,
            qf.file,
            item.put_headers,
            (loaded, total) => {
              const now = performance.now();
              const prev = speedRef.current[qf.id];
              const dt = (now - prev.lastTs) / 1000;
              const delta = loaded - prev.lastLoaded;
              const speedBps = dt > 0 ? delta / dt : 0;
              if (dt > 0.2) {
                speedRef.current[qf.id] = { lastLoaded: loaded, lastTs: now };
              }
              loadedById[qf.id] = loaded;
              const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
              dispatch({
                type: "SET_PROGRESS",
                id: qf.id,
                progress: { pct, loaded, total, speedBps, status: "uploading" },
              });
              updateOverall();
            },
            { signal: controller.signal },
          );
          dispatch({
            type: "SET_PROGRESS",
            id: qf.id,
            progress: {
              pct: 100,
              loaded: qf.file.size,
              total: qf.file.size,
              speedBps: 0,
              status: "done",
            },
          });
        } catch (err) {
          failed.push(qf.id);
          const message = err instanceof Error ? err.message : "Upload failed";
          dispatch({
            type: "SET_PROGRESS",
            id: qf.id,
            progress: {
              pct: -1,
              loaded: 0,
              total: qf.file.size,
              speedBps: 0,
              status: "failed",
              error: message,
            },
          });
        }
      });

      await runWithConcurrency(tasks, UPLOAD_CONCURRENCY);
      if (controller.signal.aborted) return;

      if (failed.length) {
        dispatch({
          type: "SET_ERROR",
          error: "Some files failed to upload. Remove them and try again.",
        });
        return;
      }

      dispatch({ type: "SET_PHASE", phase: "finishing" });
      const finishRes = await api.finishUpload(startRes.upload_id, {
        files: canonicalFiles,
        note,
      });
      if (controller.signal.aborted) return;

      dispatch({
        type: "SET_DONE",
        uploadId: finishRes.upload_id,
        gcsPrefix: finishRes.gcs_prefix,
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = err instanceof Error ? err.message : "Upload failed. Please try again.";
      dispatch({ type: "SET_ERROR", error: message });
    }
  }, []);

  return { ...state, start, reset };
}
